import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { UserPlus, X, Trash2, Shield, Eye, Edit2, Mail } from 'lucide-react';
import { InviteMemberDialog } from './InviteMemberDialog';
import { httpsCallable } from '@/lib/callableProxy';
import { functions as firebaseFunctions, db } from '@/config/firebase';
import { collection, query, getDocs, where } from 'firebase/firestore';
import { WorkspaceInviteFirestore } from '@/models/invitation_placeholder'; // We'll define a minimal frontend model or use shared type
import { toast as sonnerToast } from "sonner";
import { useAuth } from '@/contexts/AuthContext';

// Temporary interface until we import shared types properly in frontend
interface Member {
    userId: string;
    email: string;
    role: 'owner' | 'admin' | 'editor' | 'viewer';
    joinedAt: any;
}

interface Invite {
    id: string;
    email: string;
    role: string;
    status: string;
    createdAt: any;
}

interface MembersListProps {
    workspaceId: string;
    currentUserRole: string;
}

const roleIcons = {
    owner: <Shield className="h-4 w-4 text-yellow-500" />,
    admin: <Shield className="h-4 w-4 text-blue-500" />,
    editor: <Edit2 className="h-4 w-4 text-green-500" />,
    viewer: <Eye className="h-4 w-4 text-gray-500" />
};

export const MembersList: React.FC<MembersListProps> = ({ workspaceId, currentUserRole }) => {
    const [members, setMembers] = useState<Member[]>([]);
    const [invites, setInvites] = useState<Invite[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
    const { currentUser } = useAuth();

    const canManage = ['owner', 'admin'].includes(currentUserRole);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            // Fetch Members
            const membersSnap = await getDocs(collection(db, 'workspaces', workspaceId, 'members'));
            const membersData = membersSnap.docs.map(doc => ({ ...doc.data(), userId: doc.id } as Member));

            setMembers(membersData);

            if (canManage) {
                // Fetch Pending Invites (admins/owners only per Firestore rules)
                const invitesQuery = query(collection(db, 'invitations'), where('workspaceId', '==', workspaceId), where('status', '==', 'pending'));
                const invitesSnap = await getDocs(invitesQuery);
                const invitesData = invitesSnap.docs.map(doc => ({ ...doc.data(), id: doc.id } as Invite));
                setInvites(invitesData);
            } else {
                setInvites([]);
            }
        } catch (err) {
            console.error("Error fetching members:", err);
            sonnerToast.error("Could not load member list.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (workspaceId) fetchData();
    }, [workspaceId, canManage]);

    const handleRemoveMember = async (targetUserId: string) => {
        if (!confirm("Are you sure you want to remove this member?")) return;
        const toastId = sonnerToast.loading("Removing member...");
        try {
            const removeFn = httpsCallable(firebaseFunctions, 'removeWorkspaceMember');
            await removeFn({ workspaceId, targetUserId });
            sonnerToast.success("Member removed.", { id: toastId });
            fetchData();
        } catch (error: any) {
            sonnerToast.error(`Failed to remove: ${error.message}`, { id: toastId });
        }
    };

    const handleRevokeInvite = async (inviteId: string) => {
        const toastId = sonnerToast.loading("Revoking invitation...");
        try {
            const revokeFn = httpsCallable(firebaseFunctions, 'revokeInvitation');
            await revokeFn({ inviteId });
            sonnerToast.success("Invitation revoked.", { id: toastId });
            fetchData();
        } catch (error: any) {
            sonnerToast.error(`Failed to revoke: ${error.message}`, { id: toastId });
        }
    };

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <div>
                    <CardTitle className="text-xl tracking-[-0.02em] text-slate-950">Team Members</CardTitle>
                    <CardDescription className="text-slate-600">Manage who has access to this workspace.</CardDescription>
                </div>
                {canManage && (
                    <Button size="sm" className="rounded-xl bg-slate-900 text-white hover:bg-slate-800" onClick={() => setIsInviteDialogOpen(true)}>
                        <UserPlus className="mr-2 h-4 w-4" /> Invite Member
                    </Button>
                )}
            </CardHeader>
            <CardContent>
                <div className="space-y-6">
                    {/* Pending Invites Section */}
                    {invites.length > 0 && (
                        <div className="space-y-2">
                            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Pending Invitations</h4>
                            {invites.map(invite => (
                                <div key={invite.id} className="flex items-center justify-between rounded-xl border border-dashed border-slate-200 bg-slate-50/60 p-3">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white ring-1 ring-slate-200">
                                            <Mail className="h-4 w-4 text-slate-500" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium">{invite.email}</p>
                                            <div className="flex items-center gap-2">
                                                <Badge variant="outline" className="h-4 px-1 py-0 text-[10px]">{invite.role}</Badge>
                                                <span className="text-xs text-slate-500">Invited</span>
                                            </div>
                                        </div>
                                    </div>
                                    {canManage && (
                                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-red-600" onClick={() => handleRevokeInvite(invite.id)}>
                                            <X className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Active Members Section */}
                    <div className="space-y-2">
                        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Active Members ({members.length})</h4>
                        {members.map(member => (
                            <div key={member.userId} className="flex items-center justify-between rounded-xl p-3 transition-colors hover:bg-slate-50">
                                <div className="flex items-center gap-3">
                                    <Avatar className="h-9 w-9">
                                        <AvatarFallback className="bg-slate-900/5 text-slate-900 text-xs">
                                            {member.email.substring(0, 2).toUpperCase()}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div>
                                        <p className="text-sm font-medium flex items-center gap-2">
                                            {member.email}
                                            {member.userId === currentUser?.uid && <span className="text-xs text-slate-500">(You)</span>}
                                        </p>
                                        <div className="flex items-center gap-1.5 mt-0.5">
                                            {roleIcons[member.role as keyof typeof roleIcons]}
                                            <span className="text-xs capitalize text-slate-500">{member.role}</span>
                                        </div>
                                    </div>
                                </div>
                                {canManage && member.role !== 'owner' && member.userId !== currentUser?.uid && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-red-600" onClick={() => handleRemoveMember(member.userId)}>
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Remove from workspace</TooltipContent>
                                    </Tooltip>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </CardContent>

            <InviteMemberDialog 
                isOpen={isInviteDialogOpen} 
                onOpenChange={setIsInviteDialogOpen} 
                workspaceId={workspaceId}
                onInviteSent={fetchData}
            />
        </Card>
    );
};
